import { getCanonicalGpuName } from "utils/gpuNames";
import {
  RAPIDS_BASE_IMAGE_ARCH,
  RAPIDS_BASE_IMAGE_PATH,
  RAPIDS_BASE_IMAGE_REPOSITORY,
  rapidsBaseImageOptions,
} from "./rapidsBaseImages.generated";
import type {
  RapidsBaseImageSelection,
  RapidsReleaseVersion,
} from "./rapidsBaseImages.generated";

export { rapidsBaseImageOptions };
export type { RapidsBaseImageSelection, RapidsReleaseVersion };

export const rapidsReleaseVersions = Array.from(
  new Set(rapidsBaseImageOptions.map((option) => option.rapidsVersion)),
) as RapidsReleaseVersion[];

const rapidsPythonVersionsByRelease: Record<string, Set<string>> = {};
const rapidsCudaVersionsByReleaseAndPython: Record<string, Set<string>> = {};

for (const {
  rapidsVersion,
  pythonVersion,
  cudaVersion,
} of rapidsBaseImageOptions) {
  (rapidsPythonVersionsByRelease[rapidsVersion] ??= new Set()).add(
    pythonVersion,
  );
  (rapidsCudaVersionsByReleaseAndPython[`${rapidsVersion}|${pythonVersion}`] ??=
    new Set()).add(cudaVersion);
}

// CUDA 13 drops pre-Turing NVIDIA GPUs; default them to CUDA 12.
// Store names after getCanonicalGpuName normalization.
const CUDA13_UNSUPPORTED_GPU_NAMES = [
  "V100",
  "M60",
  "K80",
  "P100",
  "P4",
  "K520",
] as const;

const cuda13UnsupportedGpuNames = new Set(
  CUDA13_UNSUPPORTED_GPU_NAMES.map((name) => name.toLowerCase()),
);

export const isCuda13UnsupportedGpu = (
  gpuName: string | undefined,
): boolean => {
  // Missing GPU info, including bare "NVIDIA", defaults to CUDA 12.
  const canonical = getCanonicalGpuName(gpuName).toLowerCase();
  return canonical === "" || cuda13UnsupportedGpuNames.has(canonical);
};

export const isCuda13Version = (cudaVersion: string): boolean =>
  cudaVersion === "13" || cudaVersion.startsWith("13.");

const defaultRapidsReleaseVersion =
  rapidsReleaseVersions[0] as RapidsReleaseVersion;

export const getRapidsPythonVersions = (
  rapidsVersion: RapidsReleaseVersion,
): string[] => [...(rapidsPythonVersionsByRelease[rapidsVersion] ?? [])];

export const getRapidsCudaVersions = (
  rapidsVersion: RapidsReleaseVersion,
  pythonVersion: string,
): string[] => [
  ...(rapidsCudaVersionsByReleaseAndPython[
    `${rapidsVersion}|${pythonVersion}`
  ] ?? []),
];

export const getDefaultRapidsPythonVersion = (
  rapidsVersion: RapidsReleaseVersion,
): string => getRapidsPythonVersions(rapidsVersion)[0];

export const getAvailableRapidsCudaVersions = (
  rapidsVersion: RapidsReleaseVersion,
  pythonVersion: string,
  gpuName?: string,
): string[] => {
  const versions = getRapidsCudaVersions(rapidsVersion, pythonVersion);
  return isCuda13UnsupportedGpu(gpuName)
    ? versions.filter((version) => !isCuda13Version(version))
    : versions;
};

export const getDefaultRapidsCudaVersion = (
  gpuName: string | undefined,
  rapidsVersion: RapidsReleaseVersion,
  pythonVersion: string,
): string =>
  getAvailableRapidsCudaVersions(rapidsVersion, pythonVersion, gpuName)[0];

export const buildRapidsBaseImageTag = ({
  rapidsVersion,
  cudaVersion,
  pythonVersion,
}: RapidsBaseImageSelection): string =>
  `${rapidsVersion}-cuda${cudaVersion}-py${pythonVersion}-${RAPIDS_BASE_IMAGE_ARCH}`;

export const buildRapidsBaseImage = (
  selection: RapidsBaseImageSelection,
): string =>
  `${RAPIDS_BASE_IMAGE_REPOSITORY}:${buildRapidsBaseImageTag(selection)}`;

const rapidsBaseImageOptionsByTag: Record<
  string,
  RapidsBaseImageSelection | undefined
> = {};

for (const option of rapidsBaseImageOptions) {
  const tag = buildRapidsBaseImageTag(option);
  rapidsBaseImageOptionsByTag[tag] = option;
  rapidsBaseImageOptionsByTag[tag.replace(`-${RAPIDS_BASE_IMAGE_ARCH}`, "")] =
    option;
}

const getRapidsBaseImageTag = (image: string): string | undefined => {
  const tagPrefix = `${RAPIDS_BASE_IMAGE_PATH}:`;
  const tagStart = image.lastIndexOf(tagPrefix);
  if (tagStart === -1) return undefined;
  if (tagStart > 0 && image[tagStart - 1] !== "/") return undefined;

  return image.slice(tagStart + tagPrefix.length);
};

export const parseRapidsBaseImage = (
  image: string,
): RapidsBaseImageSelection | undefined => {
  const tag = getRapidsBaseImageTag(image);
  return tag ? rapidsBaseImageOptionsByTag[tag] : undefined;
};

export const isRapidsBaseImage = (image: string): boolean =>
  getRapidsBaseImageTag(image) !== undefined;

export const getDefaultRapidsBaseImageSelection = (
  gpuName?: string,
): RapidsBaseImageSelection => {
  const rapidsVersion = defaultRapidsReleaseVersion;
  const pythonVersion = getDefaultRapidsPythonVersion(rapidsVersion);
  const cudaVersion = getDefaultRapidsCudaVersion(
    gpuName,
    rapidsVersion,
    pythonVersion,
  );

  return { rapidsVersion, pythonVersion, cudaVersion };
};

export const getRapidsBaseImageSelectionFromImage = (
  image: string,
  gpuName?: string,
): RapidsBaseImageSelection =>
  parseRapidsBaseImage(image) ?? getDefaultRapidsBaseImageSelection(gpuName);

export const defaultRapidsBaseImageSelection =
  getDefaultRapidsBaseImageSelection();

export const defaultRapidsBaseImage = buildRapidsBaseImage(
  defaultRapidsBaseImageSelection,
);
